import { useState, useEffect } from 'react';
import { useCurrency } from './useCurrency';
import { usePricing } from './usePricing';
import { apiCreateCheckout, apiCreateCheckoutBridgeToken, apiLogInAppCheckoutEvent } from '../utils/api';
import { detectInAppBrowser } from '../utils/inAppBrowser';

// Shared "buy 2TB now" flow for the hero (HomePage.jsx) and the
// post-registration upsell (PostRegisterUpsellPage.jsx) — 2026-08-28.
// Deliberately mirrors PlansPage.jsx's startCheckout/handleBuy exactly
// (same currency source, same in-app-browser gate via
// InAppBrowserWarning/detectInAppBrowser), duplicated rather than
// imported because PlansPage.jsx itself is out of scope tonight and
// wasn't built to export this as a standalone hook. Keep this in sync
// with PlansPage.jsx by hand if that file's checkout logic ever changes.
//
// `campaign` is one of routes/payments.js's ALLOWED_CAMPAIGNS — stored in
// the Stripe session's metadata for attribution, never trusted back from
// the client. `plan` defaults to 'lifetime_2tb' (every caller before
// 2026-08-28's purchase-intent generalization was 2TB-only); AutoCheckoutPage
// passes 'lifetime_1tb' explicitly for the 1TB entry points.
export function useStartCheckout2TB(campaign, plan = 'lifetime_2tb') {
  // `loading` here is currency DETECTION in flight (fresh mount, e.g.
  // AutoCheckoutPage firing on mount right after registration) — distinct
  // from this hook's own `loading` state below (checkout session creation
  // in flight). Confusing them was the bug: reading `currency` at
  // mount-time captured useCurrency's synchronous FALLBACK_CURRENCY
  // ('USD') because the geo-IP detection fetch hadn't resolved yet, so an
  // Italian buyer's auto-fired checkout opened a USD session
  // (2026-08-28). Fixed by deferring the actual checkout call until
  // detection has resolved, via the `pending` flag below.
  const { currency, loading: currencyLoading } = useCurrency();
  // Solo per il valore da mandare al pixel: il checkout vero il prezzo non lo
  // riceve da qui, lo decide il server.
  const { priceOf } = usePricing(currency);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  // Vero mentre si sta aprendo Chrome: la pagina lo usa per spiegare cosa
  // sta succedendo, invece di lasciare la persona davanti alla domanda di
  // Facebook senza sapere cosa rispondere.
  const [saltoInCorso, setSaltoInCorso] = useState(false);
  // Il ponte a mano: compare SOLO quando il salto automatico non e'
  // possibile (iPhone) o non e' andato (Chrome assente, intent bloccato,
  // oppure la persona ha risposto "no" alla domanda di Facebook). Le pagine
  // lo disegnano gia' tutte: {inAppWarningProps && <InAppBrowserWarning/>}.
  const [avvisoPonte, setAvvisoPonte] = useState(null);

  // InitiateCheckout AL PIXEL META (2026-09-07).
  //
  // Il pixel mandava solo CompleteRegistration (useAuth) e Purchase
  // (PaymentPages): fra "si e' registrato" e "ha pagato" Meta non vedeva
  // niente. Con 197 registrazioni dagli annunci e 10 acquisti in 14 giorni,
  // Purchase da solo e' un segnale troppo raro perche' l'ottimizzazione ci
  // costruisca sopra qualcosa; InitiateCheckout ne porta ~10 volte tanti
  // (99 persone hanno aperto un checkout nello stesso periodo).
  //
  // STA IN proceed(), NON IN startCheckout(): proceed() e' il punto in cui il
  // clic diventa intenzione d'acquisto, e viene attraversato UNA volta per
  // clic da tutti e sette i punti d'acquisto del sito. startCheckout() invece
  // viene richiamato una seconda volta da chi prosegue dal ponte, e avrebbe
  // contato due volte la stessa persona.
  //
  // Nessun eventID: non esiste un gemello lato server da deduplicare. Se un
  // domani si aggiunge InitiateCheckout anche alla CAPI, va messo qui un id
  // condiviso, come gia' fanno CompleteRegistration e Purchase.
  //
  // Il prezzo arriva dalla stessa fonte che decide cosa si paga (usePricing →
  // /api/geo), mai scritto a mano: un valore diverso da quello addebitato
  // insegnerebbe a Meta un ritorno sbagliato. Se non e' ancora pronto si manda
  // comunque l'evento senza valore — meglio un evento senza importo che
  // nessun evento.
  function segnalaInizioCheckout() {
    try {
      if (typeof window.fbq !== 'function') return;
      const p = priceOf?.(plan, currency);
      const dati = { content_type: 'product', content_ids: [plan], content_name: plan };
      if (p?.amount != null) { dati.value = p.amount; dati.currency = currency; }
      window.fbq('track', 'InitiateCheckout', dati);
    } catch { /* la misura non deve mai impedire un acquisto */ }
  }

  async function startCheckout() {
    setError(''); setLoading(true);
    try {
      const { url } = await apiCreateCheckout(plan, currency.toLowerCase(), null, campaign);
      window.location.href = url;
    } catch (err) {
      // SESSIONE FANTASMA (2026-09-05): un token in localStorage il cui
      // account non esiste piu' — cancellato dal cleanup, o scaduto — fa
      // rispondere al checkout 404 "User not found" / 401 "Invalid or
      // expired token". Prima l'avviso in-app intercettava la chiamata; da
      // quando e' stato tolto, l'errore finiva in faccia a chi voleva
      // comprare (segnalato dal vivo nella fascia). Non e' un errore da
      // mostrare: si azzera la sessione morta e si manda a /register con
      // l'intenzione d'acquisto, che fara' partire il checkout DOPO la
      // registrazione (AutoCheckoutPage) — esattamente il percorso
      // dell'anonimo. Vale per fascia, hero e card, che passano tutte di qui.
      if (err.status === 401 || err.status === 404) {
        try {
          localStorage.removeItem('zv_token');
          localStorage.removeItem('zv_user');
          sessionStorage.removeItem('zv_session_pwd');
        } catch {}
        const stato = { page: 'register', entrata: campaign || null, buyIntent: { plan, campaign } };
        try {
          window.history.pushState(stato, '', '/register');
          window.dispatchEvent(new PopStateEvent('popstate', { state: stato }));
        } catch {}
        return;
      }
      setError(err.message);
      setLoading(false);
    }
  }

  // ── SALTO AUTOMATICO SU CHROME, senza chiedere niente (2026-09-06) ───────
  //
  // Il 5 settembre alle 10:49 e' stato tolto l'avviso "Apri in Chrome /
  // Continua comunque" dal percorso di pagamento, ed era giusto: un pannello
  // con due pulsanti in mezzo all'acquisto e' un ostacolo. Ma quell'avviso
  // non era solo un avviso — era il PONTE che spostava la persona su un
  // browser vero prima di creare la sessione di pagamento. Tolto lui, tutti
  // restano dentro il webview di Facebook, e li' non si paga:
  //
  //   browser normale            601 checkout ->  60 pagati  (10,0%)
  //   dentro Facebook/Instagram   58 checkout ->   2 pagati  ( 3,4%)
  //   di cui Android              40 checkout ->   0 pagati  ( ZERO )
  //
  // E la quota di checkout aperti dentro Facebook e' passata da 0-19% al
  // giorno a 65% il 5 settembre (dalle 12 in poi) e 89% il 6.
  //
  // Qui il ponte torna, ma MUTO: niente pannello, niente domande. Su Android
  // dentro Facebook/Instagram si conia il gettone-ponte gia' esistente e si
  // apre Chrome con un intent://, portandosi dietro la sessione. Chi tocca
  // "Acquista" si ritrova nel pagamento, in un browser vero, senza aver
  // deciso niente.
  //
  // MAI UN VICOLO CIECO: se il salto non avviene (webview che blocca gli
  // intent, Chrome non installato, oppure la persona risponde "no" alla
  // domanda di Facebook), si prosegue col pagamento dentro il browser interno
  // esattamente come adesso, e il mancato salto viene registrato per poterlo
  // misurare. Sui tempi vedi il commento sul ripiego piu' sotto.
  //
  // iPhone non e' toccato: da li' non esiste un modo programmatico di
  // uscire, e con 8 checkout in 30 giorni non vale un percorso a parte.
  function nuovoCorrelationId() {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  async function saltaSuChromeOppureProsegui() {
    const { isInApp, os } = detectInAppBrowser();
    if (!isInApp || os !== 'android') return startCheckout();

    setError(''); setLoading(true);
    const correlationId = nuovoCorrelationId();
    let token;
    try {
      ({ token } = await apiCreateCheckoutBridgeToken(plan, correlationId, campaign));
    } catch {
      // Visitatore anonimo (il gettone vuole un account), rete, qualunque
      // cosa: si paga come prima. Il salto e' un miglioramento, non una
      // condizione per comprare.
      return startCheckout();
    }

    const indirizzo = `${window.location.origin}/checkout?t=${encodeURIComponent(token)}`;
    const intento = `intent://${window.location.host}/checkout?t=${encodeURIComponent(token)}`
      + `#Intent;scheme=https;package=com.android.chrome;`
      + `S.browser_fallback_url=${encodeURIComponent(indirizzo)};end`;

    // OTTO SECONDI, non uno e mezzo (corretto il 2026-09-06, segnalato dal
    // vivo). Facebook mostra sempre una sua finestrella — "Vuoi uscire dal
    // sito?" — quando una pagina prova ad aprire un'altra app: e' una difesa
    // del suo browser interno, non si puo' togliere da qui. Mentre quella
    // finestrella e' aperta la pagina resta 'visible', quindi un ripiego a
    // 1,5 secondi scattava SEMPRE: il pagamento partiva dentro il webview
    // sotto la domanda, e chi poi toccava "Esci" si ritrovava due pagamenti
    // aperti. Otto secondi bastano a leggere e rispondere; chi risponde di
    // si' fa sparire la pagina (visibilityState 'hidden') e il ripiego non
    // parte affatto.
    // Basta che la pagina sia sparita UNA VOLTA perche' il ripiego sia
    // annullato per sempre: se il salto e' riuscito e la persona torna
    // sull'app di Facebook entro gli otto secondi, questa pagina risulta di
    // nuovo in primo piano e senza questo guardiano farebbe partire un
    // SECONDO pagamento dentro il webview, mentre il primo la aspetta in
    // Chrome.
    let uscitoAlmenoUnaVolta = false;
    const rientro = setTimeout(() => {
      if (uscitoAlmenoUnaVolta) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        setSaltoInCorso(false);
        setLoading(false);
        apiLogInAppCheckoutEvent('salto_non_riuscito', plan, correlationId).catch(() => {});
        // NON si crea la sessione di pagamento qui dentro (corretto il
        // 2026-09-06 dopo l'analisi dei numeri): una sessione che nasce nel
        // webview e' una sessione che quasi certamente non verra' pagata,
        // perche' il 3D Secure — obbligatorio in Europa — apre una finestra
        // che qui spesso non funziona. Misurato sui clienti veri: 32 sessioni
        // nate nel webview in 30 giorni, 2 pagate; 604 nate in un browser
        // normale, 61 pagate. Si offre invece il ponte a mano, e chi insiste
        // puo' sempre proseguire qui ("Continua comunque") — mai un vicolo
        // cieco, ma nemmeno una porta che si apre da sola su quella sbagliata.
        mostraPonteAMano();
      }
    }, 8000);
    const seNeVa = () => {
      if (document.visibilityState === 'hidden') { uscitoAlmenoUnaVolta = true; clearTimeout(rientro); }
    };
    document.addEventListener('visibilitychange', seNeVa);
    window.addEventListener('pagehide', () => { uscitoAlmenoUnaVolta = true; clearTimeout(rientro); }, { once: true });

    setSaltoInCorso(true);
    window.location.href = intento;
  }

  function mostraPonteAMano() {
    const { os } = detectInAppBrowser();
    setAvvisoPonte({
      os, plan, campaign,
      onContinue: () => { setAvvisoPonte(null); startCheckout(); },
      onClose: () => { setAvvisoPonte(null); setLoading(false); },
    });
  }

  // ORDINE, ed e' il punto di tutto (ripristinato il 2026-09-06): dentro il
  // browser interno di Facebook/Instagram la sessione Stripe NON deve
  // nascere qui. Prima si porta la persona in un browser vero — da solo su
  // Android, a mano altrove — e la sessione nasce li'. Era il comportamento
  // fino al 5 settembre alle 10:49 (commit eadd926, che lo tolse ritenendo
  // che il webview funzionasse); i numeri puliti dicono il contrario, e che
  // da allora la quota di sessioni nate nel webview e' passata dal 7% al 69%.
  function proceed() {
    segnalaInizioCheckout();
    const { isInApp, os } = detectInAppBrowser();
    if (!isInApp) return startCheckout();
    if (os === 'android') return saltaSuChromeOppureProsegui();
    // iPhone: nessun modo programmatico di uscire dal webview, quindi il
    // ponte si offre subito, con il link da aprire in Safari.
    setError(''); setLoading(true);
    return mostraPonteAMano();
  }

  function buy2tb() {
    if (currencyLoading) { setLoading(true); setPending(true); return; }
    proceed();
  }

  // Fires once currency detection resolves, only if buy2tb() was called
  // while it was still in flight — real clicks made after detection has
  // already settled (the common case) skip this and go straight through
  // buy2tb() → proceed() above.
  useEffect(() => {
    if (pending && !currencyLoading) {
      setPending(false);
      proceed();
    }
    // eslint-disable-next-line
  }, [currencyLoading, pending]);

  // inAppWarningProps torna a essere popolato (2026-09-06): i vari
  // {inAppWarningProps && <InAppBrowserWarning/>} gia' presenti nelle pagine
  // tornano vivi da soli, senza toccarne nemmeno uno.
  return { buy2tb, loading, error, saltoInCorso, inAppWarningProps: avvisoPonte };
}
